import { type FC, useState } from "react";
import {
  Dialog,
  DialogContent,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  type SvgIconProps,
} from "@mui/material";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import NoteAddOutlinedIcon from "@mui/icons-material/NoteAddOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import BedtimeOutlinedIcon from "@mui/icons-material/BedtimeOutlined";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  marketingErrorKey,
  useCreateBroadcastMutation,
} from "@/partner/entities/marketing";
import type { MutationErrorLike } from "@/partner/shared/lib";
import { DialogHeader } from "@/shared/components";
import {
  BROADCAST_TEMPLATES,
  type BroadcastTemplateId,
  type BroadcastTemplateOption,
} from "../model/broadcastLabels";

const TEMPLATE_ICONS: Record<BroadcastTemplateId, FC<SvgIconProps>> = {
  blank: NoteAddOutlinedIcon,
  announcement: CampaignOutlinedIcon,
  buyersNews: ShoppingBagOutlinedIcon,
  winBack: BedtimeOutlinedIcon,
};

interface CreateBroadcastDialogProps {
  onClose: () => void;
  /** Рассылка создана: список переключает адрес на ?tab=edit&id=. */
  onCreated: (id: string) => void;
}

/**
 * Выбор начала рассылки (?tab=create): пустая или по шаблону. Шаблон
 * бэкенда создаёт письмо и аудиторию сразу, дальше рассылка правится в
 * BroadcastDialog.
 */
export const CreateBroadcastDialog: FC<CreateBroadcastDialogProps> = ({
  onClose,
  onCreated,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [pending, setPending] = useState<BroadcastTemplateId | null>(null);
  const [createBroadcast, { loading }] = useCreateBroadcastMutation({
    onError: (error: MutationErrorLike) =>
      enqueueSnackbar(t(marketingErrorKey(error)), { variant: "error" }),
  });

  const onPick = async (option: BroadcastTemplateOption) => {
    setPending(option.id);
    const result = await createBroadcast({
      variables: {
        input: {
          name: t("broadcasts.create.blankName"),
          templateKey: option.templateKey,
        },
      },
    });
    setPending(null);
    const id = result.data?.createBroadcast.id;
    if (id) {
      enqueueSnackbar(t("broadcasts.create.success"), { variant: "success" });
      onCreated(id);
    }
  };

  return (
    <Dialog
      open
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogHeader
        title={t("broadcasts.create.title")}
        loading={loading}
        onClose={onClose}
      />
      <DialogContent sx={{ p: 1 }}>
        <List disablePadding>
          {BROADCAST_TEMPLATES.map((option) => {
            const Icon = TEMPLATE_ICONS[option.id];
            return (
              <ListItemButton
                key={option.id}
                disabled={loading}
                selected={pending === option.id}
                onClick={() => onPick(option)}
              >
                <ListItemIcon>
                  <Icon />
                </ListItemIcon>
                <ListItemText
                  primary={t(option.titleKey)}
                  secondary={t(option.descriptionKey)}
                />
              </ListItemButton>
            );
          })}
        </List>
      </DialogContent>
    </Dialog>
  );
};
